import React, { useMemo, useEffect, useRef } from "react";
import { DOCUMENT_BLOCK_TYPES, DOCUMENT_ALIGN } from "../../../documents/schema";
import { resolverRegistry } from "../../../documents/resolvers";
import { scrollContainerToChild } from "../utils/scrollContainerToChild";
import { sanitizeContractHtml } from "../../../utils/sanitizeHtml";

const alignMap = {
  [DOCUMENT_ALIGN.LEFT]: "text-left",
  [DOCUMENT_ALIGN.CENTER]: "text-center",
  [DOCUMENT_ALIGN.RIGHT]: "text-right",
  [DOCUMENT_ALIGN.JUSTIFY]: "text-justify",
};

/** Extract placeholder keys from raw text (e.g. "{COMPRADORES}" -> ["COMPRADORES"]) */
const getPlaceholderKeysFromText = (text) => {
  if (!text || typeof text !== "string") return [];
  const keys = [];
  text.replace(/\{(.*?)\}/g, (_, key) => {
    keys.push(key);
    return "";
  });
  return keys;
};

/** Collect all placeholder keys from a block (content + ROW items + signature_two_columns) */
const getBlockPlaceholderKeys = (block) => {
  const keys = new Set();
  if (block.content) {
    getPlaceholderKeysFromText(block.content).forEach((k) => keys.add(k));
  }
  if (block.items) {
    block.items.forEach((item) => {
      if (item.content) getPlaceholderKeysFromText(item.content).forEach((k) => keys.add(k));
    });
  }
  if (block.type === DOCUMENT_BLOCK_TYPES.SIGNATURE_TWO_COLUMNS) {
    if (block.left?.nameKey) keys.add(block.left.nameKey);
    if (block.left?.cargoKey) keys.add(block.left.cargoKey);
    if (block.right?.nameKey) keys.add(block.right.nameKey);
    if (block.right?.cargoKey) keys.add(block.right.cargoKey);
  }
  return Array.from(keys);
};

/** Mimics native text-selection highlight while a form field is focused. */
const previewFieldHighlightClass =
  "rounded-sm bg-[rgb(186,213,254)]/85 dark:bg-blue-950/55 ring-1 ring-inset ring-blue-400/35 shadow-sm transition-[background-color,box-shadow] duration-200";

const LiveContractPreview = ({
  template,
  formData,
  userConfiguration,
  scrollContainerRef = null,
  scrollToPlaceholder = null,
  highlightedPlaceholder = null,
  onScrollDone,
}) => {
  const containerRef = useRef(null);
  // -----------------------------------------------------
  // Resolve template data (delegated logic)
  // -----------------------------------------------------
  const resolver = resolverRegistry[template.id];

  const resolvedData = useMemo(() => {
    if (resolver) {
      return resolver(formData, userConfiguration);
    }

    return {
      ...(userConfiguration || {}),
      ...formData,
    };
  }, [formData, userConfiguration, resolver]);

  // -----------------------------------------------------
  // Placeholder replacement
  // -----------------------------------------------------
  const replacePlaceholders = (text) => {
    if (!text) return "";

    return text.replace(/\{(.*?)\}/g, (_, key) => {
      return resolvedData[key] ?? "";
    });
  };

  // -----------------------------------------------------
  // Scroll to placeholder when requested (template-agnostic)
  // -----------------------------------------------------
  useEffect(() => {
    if (!scrollToPlaceholder || !containerRef.current) return undefined;

    const scrollRoot = scrollContainerRef?.current;
    if (!scrollRoot) return undefined;

    const timer = requestAnimationFrame(() => {
      const el = containerRef.current?.querySelector(
        `[data-placeholder-keys~="${CSS.escape(scrollToPlaceholder)}"]`
      );
      if (el) {
        scrollContainerToChild(scrollRoot, el);
      }
      onScrollDone?.();
    });
    return () => cancelAnimationFrame(timer);
  }, [scrollToPlaceholder, scrollContainerRef, onScrollDone]);

  // -----------------------------------------------------
  // Render block (wrapped with data-placeholder-keys for scroll target)
  // -----------------------------------------------------
  const renderBlock = (block, index) => {
    const placeholderKeys = getBlockPlaceholderKeys(block);
    const keysAttr = placeholderKeys.length > 0
      ? { "data-placeholder-keys": placeholderKeys.join(" ") }
      : {};
    const isHighlighted =
      Boolean(highlightedPlaceholder) &&
      placeholderKeys.includes(highlightedPlaceholder);

    let content;
    switch (block.type) {
      case DOCUMENT_BLOCK_TYPES.TITLE:
        content = (
          <h2
            className={`font-bold text-lg mb-2 ${alignMap[block.align] || ""}`}
          >
            {replacePlaceholders(block.content)}
          </h2>
        );
        break;

      case DOCUMENT_BLOCK_TYPES.PARAGRAPH: {
        const raw = sanitizeContractHtml(replacePlaceholders(block.content));
        const alignClass = alignMap[block.align] || "";
        if (block.contentFormat === "html") {
          content = (
            <p
              className={`mb-3 whitespace-normal ${alignClass}`}
              dangerouslySetInnerHTML={{ __html: raw }}
            />
          );
        } else {
          content = (
            <p
              className={`mb-3 whitespace-pre-line ${alignClass}`}
            >
              {raw}
            </p>
          );
        }
        break;
      }

      case DOCUMENT_BLOCK_TYPES.DATE:
        content = (
          <p className={`mb-3 ${alignMap[block.align] || ""}`}>
            {replacePlaceholders(block.content)}
          </p>
        );
        break;

      case DOCUMENT_BLOCK_TYPES.SPACER:
        content = <div style={{ height: block.lines * 16 }} />;
        break;

      case DOCUMENT_BLOCK_TYPES.ROW:
        content = (
          <div className="flex items-center gap-4 mb-4">
            {block.items.map((item, i) => {
              if (item.type === DOCUMENT_BLOCK_TYPES.TITLE) {
                return (
                  <h2 key={i} className="font-bold">
                    {replacePlaceholders(item.content)}
                  </h2>
                );
              }
              return null;
            })}
          </div>
        );
        break;

      case DOCUMENT_BLOCK_TYPES.SIGNATURE_TWO_COLUMNS: {
        const left = block.left || {};
        const right = block.right || {};
        const sigLineClass = "border-b border-foreground min-w-[180px] w-full max-w-[220px] h-6 mb-2";
        content = (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-4">
            <div className="flex flex-col items-center text-center space-y-1">
              <div className={sigLineClass} aria-hidden />
              <h2 className="font-bold text-lg mb-2">{left.label || ""}</h2>
              <p className="mb-3 whitespace-pre-line">
                {replacePlaceholders(`{${left.nameKey || ""}}`)}
              </p>
              <p className="mb-3 whitespace-pre-line">
                {replacePlaceholders(`{${left.cargoKey || ""}}`)}
              </p>
            </div>
            <div className="flex flex-col items-center text-center space-y-1">
              <div className={sigLineClass} aria-hidden />
              <h2 className="font-bold text-lg mb-2">{right.label || ""}</h2>
              <p className="mb-3 whitespace-pre-line">
                {replacePlaceholders(`{${right.nameKey || ""}}`)}
              </p>
              <p className="mb-3 whitespace-pre-line">
                {replacePlaceholders(`{${right.cargoKey || ""}}`)}
              </p>
            </div>
          </div>
        );
        break;
      }

      default:
        content = null;
    }

    return (
      <div
        key={index}
        {...keysAttr}
        className={isHighlighted ? previewFieldHighlightClass : undefined}
      >
        {content}
      </div>
    );
  };

  // -----------------------------------------------------
  // Render footer (template.footer.lines with placeholder replacement)
  // -----------------------------------------------------
  const renderFooter = () => {
    if (!template.footer?.lines?.length) return null;
    return (
      <div className="mt-6 pt-4 border-t border-border space-y-1">
        {template.footer.lines.map((line, i) => {
          const footerKeys = getPlaceholderKeysFromText(line);
          const footerHighlighted =
            Boolean(highlightedPlaceholder) &&
            footerKeys.includes(highlightedPlaceholder);
          return (
            <p
              key={i}
              className={`text-sm ${alignMap[DOCUMENT_ALIGN.CENTER] || ""} ${
                footerHighlighted ? previewFieldHighlightClass : ""
              }`}
              data-placeholder-keys={footerKeys.join(" ")}
            >
              {replacePlaceholders(line)}
            </p>
          );
        })}
      </div>
    );
  };

  // -----------------------------------------------------
  // Render signatures (template.signatures with placeholder replacement)
  // -----------------------------------------------------
  const renderSignatures = () => {
    if (!template.signatures?.length) return null;
    return (
      <div className="mt-6 space-y-4">
        {template.signatures.map((sig, i) => (
          <div key={i} className="space-y-1">
            <p className="font-semibold text-sm">{sig.roleLabel}</p>
            <p className="text-sm">
              {replacePlaceholders(sig.namePlaceholder)}
            </p>
            <p className="text-sm text-muted-foreground">
              {replacePlaceholders(sig.titlePlaceholder)}
            </p>
          </div>
        ))}
      </div>
    );
  };

  // -----------------------------------------------------
  // Render full document
  // -----------------------------------------------------
  return (
    <div
      ref={containerRef}
      className="bg-white p-10 shadow rounded text-sm leading-relaxed max-w-3xl mx-auto"
    >
      {template.blocks.map(renderBlock)}
      {renderSignatures()}
      {renderFooter()}
    </div>
  );
};

export default LiveContractPreview;